import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Calendar as CalendarIcon } from '@origo_vl_banking/icons';
import RcDatePicker from 'rc-calendar/lib/Picker';
import enGB from 'rc-calendar/lib/locale/en_GB';
import isIS from 'rc-calendar/lib/locale/is_IS';
import Calendar from 'rc-calendar';
import 'rc-calendar/assets/index.css';
import { withTheme } from 'styled-components';
import { isEmpty } from 'lodash';
import './styles.css';

import LabelContainer from '../inputs/components/LabelContainer';
import StyledLabel from '../inputs/components/StyledLabel';
import Container from '../inputs/components/Container';
import {
  IconWrapper, Input, InputContainer, Pickerwrapper,
} from './styled-components';

class DatePicker extends Component {
  state = {
    value: this.props.initialDate,
    inputValue: this.props.initialDate,
    open: false,
    isEditingInput: false,
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick, false);
  }

  componentWillReceiveProps({ value }) {
    if (value && value !== this.state.value) {
      this.setState({ value, inputValue: value });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  onChange = (value) => {
    const { onDateChange } = this.props;

    this.setState({ value, inputValue: value });
    return onDateChange(value);
  };

  handleClick = (e) => {
    const { className } = e.target;
    const { open } = this.state;
    if (typeof className.includes === 'function') {
      if (
        (open && !className.includes('rc-calendar'))
        || (open && className.includes('rc-calendar-date'))
      ) {
        this.setState(prevState => ({ open: !prevState.open }));
      }
    }
  };

  handleInput = (e) => {
    const inputValue = e.target.value;

    this.setState({ inputValue });
    return this.setState({ isEditingInput: true });
  };

  handleOnBlur = () => {
    const { inputValue, value } = this.state;

    const { dateFormat, disableDates } = this.props;

    const withinRange = this.isDateWithinRange(inputValue); // Is date inside the allowed date range
    const inputDate = moment(inputValue, dateFormat); // The input value
    const isValidDate = inputDate.isValid();
    const isCorrectFormat = inputDate.format(dateFormat).length === dateFormat.length;

    if (isValidDate && isCorrectFormat) {
      if ((disableDates && withinRange.isWithinRange) || !disableDates) {
        // Certain ranges are disabled, but the provided date is within range
        // Also used when disabledDates are not toggled
        this.setState({ value: inputDate, isEditingInput: false });
        this.onChange(inputDate);

        return inputDate.format(dateFormat);
      }

      // Is not within range
      this.setState({
        value: withinRange.eligibleDate,
        inputValue: withinRange.eligibleDate
          ? withinRange.eligibleDate.format(dateFormat)
          : inputValue,
        isEditingInput: false,
      });

      this.onChange(withinRange.eligibleDate);
      return withinRange.eligibleDate.format(dateFormat);
    }

    this.onChange(moment(value, dateFormat));
    this.setState({ value, inputValue: value, isEditingInput: false });

    return moment(value, dateFormat).format(dateFormat);
  };

  isDateWithinRange = (input) => {
    const {
      dateFormat, disableDatesFrom, disableDatesTo, disableDates, name,
    } = this.props;

    const date = moment(input, dateFormat);
    if (disableDates) {
      const isToDateValid = moment(disableDatesTo, dateFormat).isValid();
      const isFromDateValid = moment(disableDatesFrom, dateFormat).isValid();

      if (isToDateValid && isFromDateValid) {
        const eligibleDate = name === 'startDate' ? disableDatesFrom : disableDatesTo;

        return {
          isWithinRange: date.isBetween(disableDatesFrom, disableDatesTo),
          eligibleDate,
        };
      }
      if (isToDateValid) {
        return {
          isWithinRange: date.isAfter(disableDatesTo),
          eligibleDate: moment(disableDatesTo),
        };
      }
      if (isFromDateValid) {
        return {
          isWithinRange: date.isBefore(disableDatesFrom),
          eligibleDate: moment(disableDatesFrom),
        };
      }
    }

    return { isWithinRange: true, eligibleDate: moment(input, dateFormat) };
  };

  disabledDate = (current, disableDatesTo, disableDatesFrom) => {
    if (!current) {
      // allow empty select
      return false;
    }

    const date = moment();
    date.hour(0);
    date.minute(0);
    date.second(0);

    if (!isEmpty(disableDatesTo) && !isEmpty(disableDatesFrom)) {
      return (
        disableDatesTo.valueOf() < current.valueOf()
        || disableDatesFrom.valueOf() > current.valueOf()
      );
    }
    if (!isEmpty(disableDatesTo)) {
      return disableDatesTo.valueOf() > current.valueOf();
    }
    if (!isEmpty(disableDatesFrom)) {
      return disableDatesFrom.valueOf() < current.valueOf();
    }

    return current.valueOf() < date.valueOf(); // can not select days before today
  };

  render() {
    const {
      label,
      disableDates,
      disableDatesFrom,
      disableDatesTo,
      width,
      calendarSize,
      name,
      locale,
      flexDirection,
      justifyContent,
      paddingRight,
      noLabelPadding,
      dateFormat,
      theme,
      showIcon,
      labelWidth,
      value: valueProp,
      displayFormat,
      showPlaceholder,
      disabled
    } = this.props;

    const { inputValue, value, isEditingInput } = this.state;
    const defaultValue = moment();

    const calendar = (
      <Calendar
        dateInputPlaceholder={dateFormat}
        disabledDate={current => disableDates && this.disabledDate(current, disableDatesTo, disableDatesFrom)}
        showWeekNumber={false}
        locale={locale === 'is' ? isIS : enGB}
        style={{
          top: 62,
          borderRadius: '0px 0px 8px 8px',
          width: calendarSize,
          boxShadow: 'none',
          borderColor: theme ? theme.borderColor : '#d1d1d1',
        }}
        defaultValue={defaultValue}
        format={dateFormat}
        showDateInput={false}
      />
    );

    const displayValue = () => {
      if (
        (moment(inputValue, dateFormat).format(dateFormat).length >= dateFormat.length
          && inputValue !== '1'
          && moment(inputValue, dateFormat).isValid())
        || (inputValue === this.state.value && inputValue !== null)
      ) {
        return moment(inputValue, dateFormat).format(displayFormat || dateFormat);
      }

      return inputValue;
    };

    return (
      <Pickerwrapper>
        <RcDatePicker
          animation="slide-down"
          calendar={calendar}
          onChange={this.onChange}
          defaultValue={defaultValue}
          value={value}
          open={this.state.open}
        >
          {() => (
            <Container
              flexDirection={flexDirection}
              justifyContent={justifyContent}
              paddingRight={paddingRight}
            >
              <LabelContainer noPadding={noLabelPadding} width={labelWidth}>
                <StyledLabel>
                  <FormattedMessage id={label} />
                </StyledLabel>
              </LabelContainer>
              <InputContainer
                width={width}
                onClick={() => !showIcon && this.setState(prevState => ({ open: !prevState.open }))}
                disabled={disabled}
              >
                <Input
                  type="text"
                  id={name}
                  name={name}
                  onChange={val => this.handleInput(val)}
                  onBlur={val => this.handleInput(val)}
                  placeholder={showPlaceholder && dateFormat}
                  value={isEditingInput ? inputValue : displayValue()}
                  onBlur={this.handleOnBlur}
                  width={width}
                  disabled={disabled}
                />
                {showIcon && (
                  <IconWrapper
                    disabled={disabled}
                    onClick={() => !disabled && this.setState(prevState => ({ open: !prevState.open }))}
                  >
                    <CalendarIcon color={disabled && theme.disabledColor}  />
                  </IconWrapper>
                )}
              </InputContainer>
            </Container>
          )}
        </RcDatePicker>
      </Pickerwrapper>
    );
  }
}

DatePicker.propTypes = {
  calendarSize: PropTypes.string,
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  disableDates: PropTypes.bool,
  disableDatesFrom: PropTypes.object,
  disableDatesTo: PropTypes.object,
  displayFormat: PropTypes.string,
  flexDirection: PropTypes.string,
  initialDate: PropTypes.object.isRequired,
  justifyContent: PropTypes.string,
  label: PropTypes.string,
  labelWidth: PropTypes.string,
  locale: PropTypes.string,
  name: PropTypes.string.isRequired,
  noLabelPadding: PropTypes.bool,
  onDateChange: PropTypes.func.isRequired,
  paddingRight: PropTypes.bool,
  showIcon: PropTypes.bool,
  theme: PropTypes.object,
  value: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showPlaceholder: PropTypes.bool,
};

DatePicker.defaultProps = {
  calendarSize: 'auto',
  dateFormat: 'DD.MM.YYYY',
  disabled: false,
  disableDates: false,
  disableDatesFrom: null,
  disableDatesTo: null,
  displayFormat: null,
  flexDirection: null,
  justifyContent: null,
  label: null,
  labelWidth: null,
  locale: 'is',
  noLabelPadding: false,
  paddingRight: false,
  showIcon: true,
  theme: {},
  value: null,
  width: '100%',
  showPlaceholder: true,
};

export default withTheme(DatePicker);
